import { type ZodError } from "zod";
import { v4 } from "uuid";

import { CardObjectSchema, type CardObjectType } from "./derivedSchema";

// A parse failure silently drops the offending card object from the returned
// list and the next debounced save will persist that reduced list to the DB
// — permanent data loss. Surface enough detail (actor label, violated field
// path, first Zod issue) that a support person can identify what the artist
// lost. A proper toast is deferred to the onError-toast round.
const describeParseFailure = (
  index: number,
  item: unknown,
  error: ZodError,
) => {
  const label =
    item && typeof item === "object" && "label" in item
      ? String((item as { label: unknown }).label)
      : "(unlabeled)";
  const type =
    item && typeof item === "object" && "type" in item
      ? String((item as { type: unknown }).type)
      : "(unknown type)";
  const first = error.issues[0];
  const path = first?.path.join(".") ?? "(unknown path)";
  const msg = first?.message ?? "(no message)";
  console.error(
    `[loader] Dropped card object at index ${index} — actor "${label}" ` +
      `(type=${type}) violated ${path}: ${msg}`,
    error,
  );

  // Also dump the raw actor JSON in a separate log line so the artist (or
  // support) can copy-paste it before the destructive save-loop finalizes
  // the loss. Without this, all the artist's configuration on that actor
  // (particle counts, colors, sequence timing, etc.) is gone permanently.
  let serialized: string;
  try {
    serialized = JSON.stringify(item, null, 2);
  } catch {
    serialized = String(item);
  }
  console.error(
    `[loader] Copy the dropped actor "${label}" settings below to preserve ` +
      `your work — support can help recreate the actor after the underlying ` +
      `data issue is fixed:\n${serialized}`,
  );
};

const parseCardObjectData = (data: unknown[]) => {
  const out = new Array<CardObjectType>();
  data.forEach((item, index) => {
    // `typeof null === "object"` (JS wart), so a null in the array would
    // otherwise pass a naive object check, spread into `{id}` (spreading
    // null is silently ignored in modern JS — no throw), and get logged as
    // an unlabeled drop. Handle non-object and null explicitly so the log
    // is precise and a single bad value can't take down the whole loader
    // via an uncaught throw.
    if (item === null || typeof item !== "object") {
      const got = item === null ? "null" : typeof item;
      console.error(
        `[loader] Dropped card object at index ${index} — not a valid ` +
          `object (got ${got})`,
      );
      // Non-object items usually have nothing recoverable (null, undefined,
      // primitives) but if something decodable is in there — a serialized
      // JSON string, a stray label — log it defensively so support has a
      // trail. Cheap and can't hurt.
      console.error(`[loader] Raw dropped value at index ${index}:`, item);
      return;
    }
    const res = CardObjectSchema.safeParse({ ...item, id: v4() });
    if (res.success) out.push(res.data);
    else describeParseFailure(index, item, res.error);
  });
  return out;
};

export const loadWrapVideoData = (data: unknown[]) => {
  const out = parseCardObjectData(data);
  return out;
};
